<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { toast } from "vue-sonner";
import Dropdown from "../components/Dropdown.vue";
import FormField from "../components/FormField.vue";
import RedistributionQueueDrawer from "../components/RedistributionQueueDrawer.vue";
import SummaryCard from "../components/SummaryCard.vue";
import SummaryCardGroup from "../components/SummaryCardGroup.vue";
import Textbox from "../components/Textbox.vue";
import { refreshIntermediateOrderSearch } from "../features/home/bulkIntermediateOrderSearch.ts";
import RedistributionRunActions from "../features/home/RedistributionRunActions.vue";
import SelectedPurchaseOrdersPanel from "../features/home/SelectedPurchaseOrdersPanel.vue";
import BulkOrderAllocationReview from "../features/intermediateOrders/BulkOrderAllocationReview.vue";
import { buildBulkOrderDateContexts } from "../features/intermediateOrders/bulkOrderDateContext.ts";
import {
  buildIntermediateOrderRedistributionRequest,
  DEFAULT_INTERMEDIATE_ORDER_DIVISIONS,
} from "../features/intermediateOrders/intermediateOrderRedistribution.ts";
import IntermediateOrderResultsPanel from "../features/intermediateOrders/IntermediateOrderResultsPanel.vue";
import { buildIntermediateOrderRows } from "../features/intermediateOrders/intermediateOrderRows.ts";
import { ALGORITHM_OPTIONS } from "../redistribution/models/AlgorithmType.ts";
import {
  DEMAND_SOURCE_OPTIONS,
  usesTrailingSqlRosWindow,
} from "../redistribution/models/DemandSourceType.ts";
import type { DropdownOption } from "../redistribution/models/DropdownOption.ts";
import { redistributeSkusByRequestBody } from "../services/apiClient.ts";
import { getSkuForecasts } from "../services/purchaseOrderService.ts";
import { useAppStore } from "../stores/app.ts";
import { useRedistributionQueueStore } from "../stores/redistributionQueueStore.ts";
import { useRedistributionStore } from "../stores/redistributionStore.ts";
import { getWarehouseKey } from "../utils/warehouseKeys.ts";

const appStore = useAppStore();
const queueStore = useRedistributionQueueStore();
const { setLastCustomRequestBody } = useRedistributionStore();
const {
  intermediateOrderSearchResults: rows,
  intermediateOrderSearching: searching,
  intermediateOrderSearchError: searchError,
  intermediateOrderBulkAllocations: bulkAllocations,
  intermediateOrderSearchDiagnostics: searchDiagnostics,
  intermediateOrderPoCube,
  rateOfSale,
  algorithmType,
  warehouseOptions,
  demandSource,
  availableDemandSources,
} = storeToRefs(appStore);

const demandSourceOptions = computed(() =>
  DEMAND_SOURCE_OPTIONS.filter((option) =>
    availableDemandSources.value.includes(option.value)
  )
);

const ROS_OPTIONS: DropdownOption[] = [
  { label: "30 Days", value: "30" },
  { label: "60 Days", value: "60" },
  { label: "90 Days", value: "90" },
];

const rateOfSaleFieldLabel = computed(() =>
  usesTrailingSqlRosWindow(demandSource.value)
    ? "Rate of Sale (days)"
    : "Coverage horizon (days)"
);

const selectedOrderKeys = ref<string[]>([]);
const isRunning = ref(false);
const runError = ref<string | undefined>();

const intermediateOrderCount = computed(
  () => new Set((rows.value ?? []).map((row) => row.intermediatePoNumber)).size
);
const skuCount = computed(
  () => new Set((rows.value ?? []).map((row) => row.skuNumber)).size
);
const totalCubicFeet = computed(() =>
  (rows.value ?? []).reduce((total, row) => total + row.totalCubicFeet, 0)
);
const warehouseCount = computed(
  () => new Set((rows.value ?? []).map((row) => row.warehouseNumber)).size
);
const inRedistributionCount = computed(
  () =>
    (rows.value ?? []).filter(
      (row) => row.distributionStatus === "In Distribution"
    ).length
);

const selectedIntermediateOrders = computed(() => {
  const selected = new Set(selectedOrderKeys.value);
  return buildIntermediateOrderRows(rows.value, warehouseOptions.value).filter(
    (row) => selected.has(row.rowKey)
  );
});

const totalSelectedItems = computed(() =>
  selectedIntermediateOrders.value.reduce(
    (sum, order) => sum + order.totalUnits,
    0
  )
);

const selectedIntermediateOrderSummaries = computed(() =>
  selectedIntermediateOrders.value.map((order) => ({
    poNumber: order.intermediatePoNumber,
    estArrival: order.lastDistributionDate || order.shipDateRangeEnd || "—",
  }))
);

function handleRefresh(): void {
  void refreshIntermediateOrderSearch(appStore);
}

function handleSelectionChanged(selected: string[]): void {
  selectedOrderKeys.value = selected;
  runError.value = undefined;
}

async function runSelectedIntermediateOrders(): Promise<void> {
  if (selectedIntermediateOrders.value.length < 2) {
    toast.warning(
      "Select at least 2 Intermediate Orders to run redistribution."
    );
    return;
  }

  if (!queueStore.canSubmit) {
    toast.warning(
      "Too many running requests. Please wait for one to complete."
    );
    return;
  }

  isRunning.value = true;
  runError.value = undefined;

  try {
    const selectedRows = selectedIntermediateOrders.value.flatMap(
      (order) => order.skus
    );
    const resolvedRateOfSale = Number(rateOfSale.value);
    const resolvedPoCube = Number(intermediateOrderPoCube.value);
    const request = buildIntermediateOrderRedistributionRequest(
      selectedRows,
      DEFAULT_INTERMEDIATE_ORDER_DIVISIONS,
      resolvedRateOfSale,
      resolvedPoCube,
      algorithmType.value,
      undefined,
      {},
      demandSource.value
    );

    if (request.purchaseOrders.length > 0) {
      const avgWOSWarehouses = [
        ...new Set(
          request.purchaseOrders.map((po) =>
            getWarehouseKey(po.division, po.warehouse)
          )
        ),
      ];
      request.avgDaysOfStockBySku = await getSkuForecasts(
        avgWOSWarehouses,
        request.purchaseOrders,
        request.rateOfSaleDays
      );
    }

    const requestBody = JSON.stringify(request, null, 2);
    setLastCustomRequestBody(requestBody);
    const submission = await redistributeSkusByRequestBody(request);
    queueStore.enqueue(
      submission.requestId,
      submission.submittedAt,
      request.purchaseOrders,
      requestBody,
      buildBulkOrderDateContexts(selectedRows)
    );
    toast.info("Intermediate Order redistribution submitted", {
      description: "You'll be notified when results are ready.",
    });
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : "Failed to submit redistribution.";
    runError.value = message;
    toast.error(`Redistribution failed: ${message}`);
  } finally {
    isRunning.value = false;
  }
}
</script>

<template>
  <div class="home-layout page-main-container">
    <div class="home-results-shell">
      <div class="page-main-container gap-5">
        <!-- Page title and subtitle live in AppHeader (route meta). -->

        <SummaryCardGroup>
          <SummaryCard
            label="Intermediate Orders"
            :value="intermediateOrderCount"
            variant="info"
          />
          <SummaryCard label="Total SKUs" :value="skuCount" variant="info" />
          <SummaryCard
            label="Total Cubic Feet"
            :value="totalCubicFeet.toFixed(1)"
            variant="info"
          />
          <SummaryCard
            label="Warehouses"
            :value="warehouseCount"
            variant="info"
          />
          <SummaryCard
            label="In Redistribution"
            :value="inRedistributionCount"
            variant="improved"
          />
        </SummaryCardGroup>

        <IntermediateOrderResultsPanel
          :rows="rows"
          :loading="searching"
          :error="searchError"
          @refresh="handleRefresh"
          @selection-changed="handleSelectionChanged"
        />

        <BulkOrderAllocationReview
          v-if="
            !searching &&
            !searchError &&
            (searchDiagnostics || bulkAllocations.length > 0)
          "
          :diagnostics="searchDiagnostics"
          :allocations="bulkAllocations"
          :show-first-cost="false"
        />

        <div class="results-panel-selection-bar">
          <SelectedPurchaseOrdersPanel
            :selected-pos="selectedIntermediateOrderSummaries"
            :total-selected-items="totalSelectedItems"
            selection-noun="IO"
            items-label="units"
            empty-hint="Select at least 2 Intermediate Orders to run redistribution."
          />

          <div class="flex items-end gap-4">
            <FormField label="PO Cube Size">
              <Textbox
                v-model="intermediateOrderPoCube"
                placeholder="999999"
                label="PO Cube Size"
              />
            </FormField>

            <FormField :label="rateOfSaleFieldLabel">
              <Dropdown
                v-model="rateOfSale"
                :options="ROS_OPTIONS"
                placeholder="Select..."
              />
            </FormField>

            <FormField label="Demand">
              <Dropdown
                v-model="demandSource"
                :options="[...demandSourceOptions]"
                placeholder="Select demand source"
              />
            </FormField>

            <FormField label="Algorithm">
              <Dropdown
                v-model="algorithmType"
                :options="[...ALGORITHM_OPTIONS]"
                placeholder="Select algorithm"
              />
            </FormField>

            <RedistributionRunActions
              :is-test-mode="false"
              :is-running="isRunning"
              :is-preparing-custom-request-dialog="false"
              :selected-po-count="selectedIntermediateOrders.length"
              :run-calculation-status="undefined"
              run-button-label="⚡ Run Intermediate Order Redistribution"
              @run-algorithm="void runSelectedIntermediateOrders()"
            />
          </div>
        </div>

        <div v-if="runError" class="results-panel-error-banner" role="alert">
          ⚠️ {{ runError }}
        </div>
      </div>
    </div>

    <RedistributionQueueDrawer />
  </div>
</template>
